import CloseIcon from '@mui/icons-material/Close';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import { alpha } from '@mui/material/styles';
import { COLORS } from '@src/constants/design-tokens/colors';
import { selectFocusMode } from '@src/store/slices/focus_mode';
import {
  type MouseEvent,
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import PDFPreview from './PDFPreview';

/**
 * PDFプレビュードロワーコンポーネントのProps型定義
 */
type PdfPreviewDrawerProps = {
  open: boolean;
  onClose: () => void;
  isScrolling?: boolean;
  page?: number;
  onPageChange?: (page: number) => void;
  onPageTotalChange?: (total: number) => void;
  onPdfScrollingChange?: (isScrolling: boolean) => void;
  onPreviewGeneratingChange?: (isGenerating: boolean) => void;
  previewToken?: string | null;
};

/**
 * PDFプレビュードロワーコンポーネント
 *
 * タブレット・モバイル時に全画面でPDFプレビューを表示するモーダルです。
 * リッチなフェードイン/アウトアニメーションで表示切り替えを行います。
 *
 * @param {PdfPreviewDrawerProps} props - コンポーネントのプロパティ
 * @param {boolean} props.open - ドロワーの開閉状態
 * @param {() => void} props.onClose - ドロワーを閉じる時のコールバック
 * @param {boolean} [props.isScrolling] - スクロール中かどうか
 * @param {number} [props.page] - 現在のPDFページ番号
 * @param {(page: number) => void} [props.onPageChange] - PDFページ変更時のコールバック
 * @param {(total: number) => void} [props.onPageTotalChange] - PDF総ページ数変更時のコールバック
 * @returns {ReactElement} PDFプレビュードロワーコンポーネント
 *
 * @example
 * ```tsx
 * <PdfPreviewDrawer
 *   open={isPreviewOpen}
 *   onClose={() => setIsPreviewOpen(false)}
 *   page={pdfPage}
 *   onPageChange={handlePdfPageChange}
 *   onPageTotalChange={setPdfPageTotal}
 * />
 * ```
 */
const PdfPreviewDrawer = memo(
  ({
    open,
    onClose,
    isScrolling = false,
    page = 1,
    onPageChange,
    onPageTotalChange,
    onPdfScrollingChange,
    onPreviewGeneratingChange,
    previewToken,
  }: PdfPreviewDrawerProps): ReactElement => {
    const isFocusMode = useSelector(selectFocusMode) || false;
    const [pdfKey, setPdfKey] = useState<number>(0);
    const [showBackdrop, setShowBackdrop] = useState<boolean>(false);
    const [isPdfVisible, setIsPdfVisible] = useState<boolean>(false);
    const prevOpenRef = useRef<boolean>(open);

    // PDFレンダリング状態の変化を通知
    const handleRenderingChange = useCallback(
      (rendering: boolean) => {
        if (rendering || !previewToken) {
          return;
        }

        setShowBackdrop(true);
        setIsPdfVisible(true);
        if (onPreviewGeneratingChange) {
          onPreviewGeneratingChange(false);
        }
      },
      [onPreviewGeneratingChange, previewToken]
    );

    // PDFリセット処理
    const handlePdfReset = useCallback(() => {
      // PDFコンポーネントを完全に再マウントするためにkeyを変更
      setPdfKey((prev) => prev + 1);
      // BackdropとPDFを非表示にリセット
      setShowBackdrop(false);
      setIsPdfVisible(false);
    }, []);

    useEffect(() => {
      if (!previewToken) {
        return;
      }

      handlePdfReset();
    }, [previewToken, handlePdfReset]);

    // プレビューの開閉時にPDFをクリア
    useEffect(() => {
      if (!prevOpenRef.current && open) {
        // プレビューが開かれた - 常に新しいPDFを表示するためにリセット
        handlePdfReset();
      } else if (prevOpenRef.current && !open) {
        // プレビューが閉じられた - フェードアウト完了後にリセット
        const timer = setTimeout(() => {
          handlePdfReset();
        }, 400); // Modalのフェードアウト時間に合わせる
        return () => clearTimeout(timer);
      }
      prevOpenRef.current = open;
    }, [open, handlePdfReset]);

    const isDrawerVisible = Boolean(previewToken) && open;

    return (
      <Modal
        open={isDrawerVisible}
        onClose={onClose}
        disableScrollLock
        closeAfterTransition
        disableEnforceFocus
        sx={{
          display: { xs: 'block', sm: 'block', md: 'block', lg: 'none' },
          zIndex: (theme) => theme.zIndex.modal + 10000,
        }}
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 400,
            sx: {
              backgroundColor: alpha(COLORS.darkMode.background.default, 0.85),
              backdropFilter: 'blur(4px)',
              // PDFレンダリング完了までBackdropを非表示
              opacity: showBackdrop ? 1 : 0,
              transition: 'opacity 0.3s ease',
            },
          },
        }}
      >
        <Fade in={isDrawerVisible} timeout={400}>
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              background: isFocusMode
                ? 'linear-gradient(135deg, rgba(10, 10, 10, 0.95), rgba(30, 30, 30, 0.98))'
                : 'linear-gradient(135deg, rgba(14, 23, 38, 0.98), rgba(26, 32, 44, 0.98))',
              boxSizing: 'border-box',
              boxShadow: '0 20px 80px rgba(0, 0, 0, 0.6)',
              outline: 'none',
              opacity: isPdfVisible ? 1 : 0,
              pointerEvents: isPdfVisible ? 'auto' : 'none',
              transition: 'opacity 0.25s ease',
            }}
          >
            {/* 閉じるボタン */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 16 },
                right: { xs: 8, sm: 16 },
                zIndex: 1000,
              }}
            >
              <IconButton
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  // aria-hidden警告を防ぐため、閉じる前にフォーカスを解除
                  e.currentTarget.blur();
                  onClose();
                }}
                aria-label="プレビューを閉じる"
                sx={{
                  backgroundColor: isFocusMode
                    ? alpha(COLORS.darkMode.text.primary, 0.1)
                    : alpha(COLORS.text.primary, 0.05),
                  color: isFocusMode ? COLORS.darkMode.text.primary : COLORS.text.primary,
                  '&:hover': {
                    backgroundColor: isFocusMode
                      ? alpha(COLORS.darkMode.text.primary, 0.2)
                      : alpha(COLORS.text.primary, 0.1),
                  },
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* PDFプレビュー */}
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                width: '100%',
                height: '100%',
              }}
            >
              {/* PDFは常にマウントしてレンダリングを進める */}
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  visibility: isPdfVisible ? 'visible' : 'hidden',
                  opacity: isPdfVisible ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                <PDFPreview
                  key={`${previewToken || 'pdf'}-${pdfKey}`}
                  isScrolling={isScrolling}
                  hideControls={false}
                  page={page}
                  onPageChange={onPageChange}
                  onPageTotalChange={onPageTotalChange}
                  onPdfScrollingChange={onPdfScrollingChange}
                  onRenderingChange={handleRenderingChange}
                />
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>
    );
  }
);

PdfPreviewDrawer.displayName = 'PdfPreviewDrawer';

export default PdfPreviewDrawer;
